import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Search, Plus, X, Dumbbell, Wind, ShieldCheck, Heart, Zap } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['STRENGTH', 'PLYOMETRIC', 'AEROBIC', 'RTP', 'RECOVERY'];
const CAT_COLORS = {
  STRENGTH: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  PLYOMETRIC: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  AEROBIC: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  RTP: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  RECOVERY: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
};
const CAT_ICONS = { STRENGTH: Dumbbell, PLYOMETRIC: Zap, AEROBIC: Wind, RTP: ShieldCheck, RECOVERY: Heart };

export function ExerciseSelector({ open, onClose, onSelect }) {
  const { api } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const searchRef = useRef(null);

  const fetchExercises = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (catFilter !== 'ALL') params.append('category', catFilter);
      if (search) params.append('search', search);
      const res = await api.get(`/exercises?${params.toString()}`);
      setExercises(res.data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [api, catFilter, search]);

  useEffect(() => {
    if (open) {
      fetchExercises();
      setTimeout(() => searchRef.current?.focus(), 100);
    } else {
      setSearch('');
      setCatFilter('ALL');
    }
  }, [open, fetchExercises]);

  const handleSelect = (ex) => {
    onSelect({
      exercise_id: ex.id,
      exercise_name: ex.name,
      category: ex.category,
      sets: ex.default_sets || '',
      reps: ex.default_reps || '',
      duration_sec: ex.default_duration || '',
      is_vbt_linked: ex.is_vbt_linked || false,
      vbt_lift_type: ex.vbt_lift_type || null,
      load_supported: ex.load_supported !== false,
    });
    onClose();
  };

  const handleCreated = (newEx) => {
    setShowCreate(false);
    setExercises(prev => [...prev, newEx].sort((a, b) => a.name.localeCompare(b.name)));
    handleSelect(newEx);
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open && !showCreate} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[80vh] flex flex-col" data-testid="exercise-selector-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Select Exercise</DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              ref={searchRef}
              placeholder="Search exercises..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-zinc-800 border-zinc-700 text-zinc-100"
              data-testid="exercise-selector-search"
            />
          </div>

          {/* Category filter */}
          <div className="flex gap-1.5 flex-wrap">
            {['ALL', ...CATEGORIES].map(c => (
              <button
                key={c}
                onClick={() => setCatFilter(c)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-sm transition-colors ${
                  catFilter === c
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                    : 'bg-zinc-800 text-zinc-500 border border-zinc-700 hover:border-zinc-600'
                }`}
                data-testid={`selector-filter-${c.toLowerCase()}`}
              >
                {c === 'ALL' ? 'All' : c}
              </button>
            ))}
          </div>

          {/* Exercise list */}
          <div className="flex-1 overflow-y-auto space-y-1 min-h-0 max-h-[400px]" data-testid="exercise-selector-list">
            {loading ? (
              <div className="text-center py-6 text-zinc-500 text-sm">Loading...</div>
            ) : exercises.length === 0 ? (
              <div className="text-center py-6 text-zinc-500 text-sm">No exercises found</div>
            ) : (
              exercises.map(ex => {
                const Icon = CAT_ICONS[ex.category] || Dumbbell;
                return (
                  <button
                    key={ex.id}
                    onClick={() => handleSelect(ex)}
                    className="w-full text-left px-3 py-2.5 rounded-sm hover:bg-zinc-800 transition-colors flex items-center justify-between group"
                    data-testid={`selector-exercise-${ex.id}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${CAT_COLORS[ex.category]?.split(' ')[1] || 'text-zinc-400'}`} />
                      <div className="min-w-0">
                        <span className="text-sm text-zinc-200 group-hover:text-zinc-50">{ex.name}</span>
                        {ex.description && (
                          <span className="text-[10px] text-zinc-600 block truncate">{ex.description}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {ex.is_vbt_linked && <Badge className="bg-blue-500/10 text-blue-400 text-[9px]">VBT</Badge>}
                      <Badge variant="outline" className={`text-[10px] ${CAT_COLORS[ex.category]}`}>{ex.category}</Badge>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Create new */}
          <div className="border-t border-zinc-800 pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCreate(true)}
              className="w-full border-dashed border-zinc-700 text-zinc-400 hover:text-amber-400 hover:border-amber-500/50"
              data-testid="create-exercise-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Create New Exercise
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {showCreate && (
        <CreateExerciseDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </>
  );
}

function CreateExerciseDialog({ open, onClose, onCreated }) {
  const { api } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', category: 'STRENGTH', description: '', notes: '',
    default_sets: '', default_reps: '', default_duration: '',
  });

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        description: form.description || null,
        notes: form.notes || null,
        default_sets: form.default_sets ? parseInt(form.default_sets) : null,
        default_reps: form.default_reps ? parseInt(form.default_reps) : null,
        default_duration: form.default_duration ? parseInt(form.default_duration) : null,
      };
      const res = await api.post('/exercises', payload);
      toast.success('Exercise created');
      onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create exercise');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm" data-testid="create-exercise-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-100">New Exercise</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-zinc-400 text-xs">Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Back Squat"
              className="bg-zinc-800 border-zinc-700 text-zinc-100"
              autoFocus
              data-testid="new-exercise-name"
            />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Category *</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="new-exercise-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {CATEGORIES.map(c => (
                  <SelectItem key={c} value={c} className="text-zinc-100">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-zinc-100 min-h-[50px]"
              rows={2}
              data-testid="new-exercise-description"
            />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Notes</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-zinc-100"
              placeholder="Any extra notes..."
              data-testid="new-exercise-notes"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-zinc-400 text-[10px]">Default Sets</Label>
              <Input
                type="number"
                value={form.default_sets}
                onChange={(e) => setForm({ ...form, default_sets: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs"
                data-testid="new-exercise-sets"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px]">Default Reps</Label>
              <Input
                type="number"
                value={form.default_reps}
                onChange={(e) => setForm({ ...form, default_reps: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs"
                data-testid="new-exercise-reps"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px]">Default Dur (sec)</Label>
              <Input
                type="number"
                value={form.default_duration}
                onChange={(e) => setForm({ ...form, default_duration: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 h-8 text-xs"
                data-testid="new-exercise-duration"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-400" data-testid="new-exercise-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="new-exercise-save">
            {saving ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ExerciseSelector;
